import { useState, useEffect } from 'react';

function readStorage(storage, key, defaultValue, ttlMs) {
  let raw;
  try {
    raw = storage.getItem(key);
  } catch (e) {
    console.warn(`usePersistedState: could not access storage for "${key}".`, e);
    return defaultValue;
  }
  if (raw == null) return defaultValue;

  try {
    const parsed = JSON.parse(raw);
    if (ttlMs == null) return parsed;

    if (!parsed || typeof parsed !== 'object' || !('value' in parsed) || !('savedAt' in parsed)) {
      throw new Error('missing TTL envelope');
    }
    if (Date.now() - parsed.savedAt > ttlMs) {
      storage.removeItem(key);
      return defaultValue;
    }
    return parsed.value;
  } catch (e) {
    console.warn(`usePersistedState: corrupt value for "${key}", resetting.`, e);
    try { storage.removeItem(key); } catch (_) { /* ignore */ }
    return defaultValue;
  }
}

/**
 * Drop-in replacement for useState that mirrors the value to localStorage or
 * sessionStorage. Pass ttlMs to have stale persisted values discarded on read.
 */
export default function usePersistedState(key, defaultValue, options = {}) {
  const { storage: storageType = 'session', ttlMs } = options;
  const storage = storageType === 'local' ? window.localStorage : window.sessionStorage;

  const [value, setValue] = useState(() => readStorage(storage, key, defaultValue, ttlMs));

  useEffect(() => {
    try {
      const toStore = ttlMs == null ? value : { value, savedAt: Date.now() };
      storage.setItem(key, JSON.stringify(toStore));
    } catch (e) {
      console.warn(`usePersistedState: could not persist "${key}".`, e);
    }
  }, [key, value]);

  return [value, setValue];
}

export function clearPersistedKeys(keys, storageType = 'session') {
  const storage = storageType === 'local' ? window.localStorage : window.sessionStorage;
  keys.forEach(key => {
    try { storage.removeItem(key); } catch (_) { /* ignore */ }
  });
}
